import {
  Button,
  ColumnTable,
  Loading,
  Table,
  TableCollapsable,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { SectionTitle } from '../../../../components/text';
import { TranslationsKeys } from '../../../../locales/translations';
import { StudentRiskAction } from '../../types';
import { useStudentsRiskHistoryDetails } from './loaders/StudentsRiskHistoryDetailsLoader';
import { StudentsRiskHistoryDetailsOrigin } from './types';

type StudentsRiskHistoryDetailsActionsTableProps = {
  origin?: StudentsRiskHistoryDetailsOrigin;
};

const StudentsRiskHistoryDetailsActionsTable = ({
  origin = 'riskSessionHistory',
}: StudentsRiskHistoryDetailsActionsTableProps) => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_WARNINGS_STUDENT_HISTORY,
  );

  const { loading, result } = useStudentsRiskHistoryDetails();

  const texts = useMemo(() => {
    return {
      title: t('studentActions.title'),
      session: t('studentActions.table.columns.session'),
      date: t('studentActions.table.columns.date'),
      groupType: t('studentActions.table.columns.group'),
      type: t('studentActions.table.columns.type'),
      notes: t('studentActions.table.columns.notes'),
      owner: t('studentActions.table.columns.owner'),
      adminAccount: t('studentActions.table.columns.adminAccount'),
      seeMore: t('studentActions.table.columns.seeMore'),
      addAction: t('studentActions.addAction'),
      addUpdate: t('studentActions.table.actions.addUpdate'),
    };
  }, [t]);

  const columns = useMemo(() => {
    const cols: (ColumnTable<StudentRiskAction> & {
      hidden?: boolean;
    })[] = [
      {
        headerText: texts.session,
        columnName: 'session',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value?.name;
        },
      },
      {
        headerText: texts.date,
        columnName: 'date',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return dayjs(row?.createdAt).format('DD/MM/YYYY') || '-';
        },
      },
      {
        headerText: texts.groupType,
        columnName: 'groupType',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value;
        },
      },
      {
        headerText: texts.type,
        columnName: 'type',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value;
        },
      },
      {
        headerText: texts.notes,
        columnName: 'notes',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.owner,
        columnName: 'owner',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.adminAccount,
        columnName: 'adminAccount',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return JSON.stringify(value);
        },
      },
    ];
    return cols.filter((col) => !col.hidden);
  }, [texts]);

  const subColumns = useMemo(() => {
    const cols: (ColumnTable<StudentRiskAction> & {
      hidden?: boolean;
    })[] = [
      {
        headerText: texts.date,
        columnName: 'date',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return dayjs(row?.createdAt).format('DD/MM/YYYY') || '-';
        },
      },
      {
        headerText: texts.notes,
        columnName: 'notes',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.adminAccount,
        columnName: 'adminAccount',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return JSON.stringify(value);
        },
      },
    ];
    return cols.filter((col) => !col.hidden);
  }, [texts]);

  if (loading) {
    return (
      <>
        <Loading textBody={''} />
      </>
    );
  }

  if (!result?.success) {
    return <></>;
  }

  return (
    <>
      <div className="w-100 mt-4 d-block">
        <Row className="d-flex justify-content-between align-items-center mt-4">
          <Col xs={12} md={6}>
            <SectionTitle text={texts.title} />
          </Col>
          {origin !== 'studentFile' && (
            <Col
              xs={12}
              md={6}
              className="d-flex flex-row align-items-center justify-content-end"
            >
              <div style={{ maxWidth: 275 }}>
                <Button
                  text={texts.addAction}
                  size="md"
                  outlined
                  fullwidth
                  type="button"
                />
              </div>
            </Col>
          )}
          <Col className="mt-4" xs={12}>
            <TableCollapsable
              columns={columns}
              data={[]}
              isLoading={loading}
              secondColumn={{
                columnName: 'second',
                headerText: 'second',
                cellFormat: ({ row }) => (
                  <div className="w-100">
                    <Table
                      borderless={false}
                      data={[]}
                      color="transparent"
                      striped
                      columns={subColumns}
                    />
                    {origin !== 'studentFile' && (
                      <div className="w-100 d-flex flex-row align-items-center justify-content-end mt-4">
                        <Button
                          text={texts.addUpdate}
                          size="md"
                          type="button"
                        />
                      </div>
                    )}
                  </div>
                ),
              }}
            />
          </Col>
        </Row>
      </div>
    </>
  );
};

export default StudentsRiskHistoryDetailsActionsTable;
